import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Package } from '@/utils/types';
import merge from 'lodash/merge';

export type ConfigState = {
  content: any;
  packages: Package[] | [];
  missingProps: { props: string[]; catEntryId: string }[];
  defaultContents: Object | any;
  prescriptionDefaults: Object | any;
  currencyFormat?: any;
};

const initialState: ConfigState = {
  currencyFormat: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    prefix: '$',
  },
  content: {},
  packages: [],
  missingProps: [],
  defaultContents: {},
  prescriptionDefaults: {
    sphMin: -20,
    sphMax: 20,
    sphRes: 0.25,
    cylMin: -20,
    cylMax: 20,
    cylRes: 0.25,
    addMin: 0.75,
    addMax: 4,
    addRes: 0.25,
    pdHalfMin: 25,
    pdHalfMax: 38,
    pdHalfRes: 1,
    pdMin: 51,
    pdMax: 77,
    pdRes: 1,
    pdSphRangeMin: -3,
    pdSphRangeMax: 3,
    prismVertMin: 0.5,
    prismVertMax: 5,
    prismVertRes: 0.25,
    prismBaseVert: ['Up', 'Down'],
    prismHorMin: 0.5,
    prismHorMax: 5,
    prismHorRes: 0.25,
    prismBaseHor: ['In', 'Out'],
  },
};

type SetMissingPropsPayload = PayloadAction<{
  missingProps: string[];
  catEntryId: string;
}>;

type SetDefaultTitlePayload = PayloadAction<{
  property: string;
  value: string;
  sequence?: number;
}>;

const configSlice = createSlice({
  name: 'config',
  initialState: initialState,
  reducers: {
    setConfigContent: (state, action: PayloadAction<any[]>) => {
      state.content = action.payload;
    },
    setConfigPackages: (state, action: PayloadAction<any[]>) => {
      state.packages = action.payload;
    },
    setMissingProps: (state, { payload }: SetMissingPropsPayload) => {
      const { missingProps, catEntryId } = payload;
      state.missingProps.push({ catEntryId, props: missingProps });
    },
    addDefaultContent: (state, { payload }: SetDefaultTitlePayload) => {
      state.defaultContents[payload.property] = {
        ...state.defaultContents[payload.property],
        [payload.value]: {
          imageFileName: 'image',
          title:
            payload.property === 'frameColor'
              ? 'MISSING CONTENT'
              : payload.value,
          description: 'MISSING CONTENT',
          promoBadge: null,
          sequence: payload?.sequence || null,
          contentIdentifier: null,
          longDescription: null,
          badge: null,
        },
      };
    },
    setConfig: (state, { payload }: PayloadAction<any>) => {
      if (
        !payload.lensesData.packages.find(
          (pkg) => pkg.lensPackage.type === 'Frame Only'
        )
      ) {
        let pricesPlanoPkg = payload?.lensesData?.prices?.find(
          (pkg: any) =>
            pkg?.lensPackage?.productUPC === 'PLANO' ||
            pkg?.lensPackage?.upc ===
              (payload.data?.frameOnlyLensUPC ??
                window?.rxcBrain?.flowDefiningAttributes?.frameOnlyLensUPC)
        );
        const defaultFrameOnlyPackage = {
          frame: {
            offerPrice: payload.data.frame.offerPrice,
            insPrice: payload.data.frame.insPrice,
            ...(pricesPlanoPkg?.frame && { ...pricesPlanoPkg.frame }),
          },
          lensPackage: {
            upc:
              payload.data?.frameOnlyLensUPC ??
              window?.rxcBrain?.flowDefiningAttributes?.frameOnlyLensUPC,
            type:
              Object.keys(payload.lensesData?.content?.type).find((key) =>
                key?.toLowerCase()?.includes('frame')
              ) ?? 'Frame Only',
            listPrice: '0.00',
            offerPrice: '0.00',
            insPrice: window?.rxcBrain?.flowDefiningAttributes
              ?.frameOnlyInsurable
              ? null
              : '0.00',
            productUPC: 'PLANO',
          },
        };
        if (pricesPlanoPkg) {
          defaultFrameOnlyPackage.lensPackage = {
            ...defaultFrameOnlyPackage.lensPackage,
            ...pricesPlanoPkg.lensPackage,
          };
        }
        payload.lensesData.packages.push(defaultFrameOnlyPackage);
      }
      payload.lensesData.content = merge(
        payload.lensesData.content,
        state.defaultContents
      );
      if (payload.lensesData.content?.bundles) {
        payload.lensesData.content.lensBundle =
          payload.lensesData.content.bundles;
        delete payload.lensesData.content.bundles;
      }
      Object.keys(payload.lensesData.content).forEach((rootKey) => {
        payload.lensesData.content[rootKey] &&
          Object.keys(payload.lensesData.content[rootKey]).forEach(
            (innerKey) => {
              const content = payload.lensesData.content[rootKey][innerKey];
              if (!content) return;

              if (!content.title) content.title = 'MISSING CONTENT';
              if (!content.description) content.description = 'MISSING CONTENT';
            }
          );
      });

      const { missingProps } = state;
      const { lensesData } = payload;
      missingProps.forEach(({ props, catEntryId }) => {
        for (let i = 0; i < lensesData.packages.length; i++) {
          const pack = lensesData.packages[i].lensPackage;
          if (pack.catEntryId !== catEntryId) continue;
          props.forEach((prop) => {
            pack[prop] = null;
          });
          payload.lensesData.packages[i].lensPackage = pack;
        }
      });

      // replace "" with null
      for (let i = 0; i < lensesData.packages.length; i++) {
        const pack = lensesData.packages[i].lensPackage;
        Object.entries(pack).forEach(([key, val]) => {
          if (val === '') {
            payload.lensesData.packages[i].lensPackage[key] = null;
          }
        });
      }
      window.rxcBrain.initWithNewLensesData({ ...state, ...payload });
      return { ...state, ...payload };
    },
    reset: (state, { payload }: PayloadAction<string>) => {
      /* Object.keys(initialState).forEach(key => {
                state[key] = initialState[key];
            }) */
      return initialState;
    },
    setConfigRXValues: (state: any, payload: any) => {
      state.data.frame.rxValues = payload?.payload;
    },
  },
});

export const configReducer = configSlice.reducer;
export const configActions = configSlice.actions;
